import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import { buildDailyTargets } from '@/lib/tdee';
import type { Profile } from '@/types';

/**
 * Запись веса — одна для всех мест (14.10.2026).
 *
 * Раньше их было две, и они расходились: Профиль пересчитывал норму от нового
 * веса, а строка на главной писала только вес — норма оставалась от старого.
 * Теперь и плитка веса в Профиле, и напоминание на главной открывают одно окно
 * (`WeightEntrySheet`) и сохраняют через эту функцию.
 */
export async function saveWeight(profile: Profile, weightKg: number): Promise<Profile | null> {
  if (!(await requireSession())) return null;
  const { error: logErr } = await supabase.from('weight_log').insert({ user_id: profile.id, weight_kg: weightKg });
  if (logErr) throw logErr;

  // Норма пересчитывается, только если есть всё для расчёта. Ручная норма
  // (use_custom_targets) не трогается: она лежит в своих колонках.
  const canRecalc = profile.sex && profile.birth_date && profile.height_cm && profile.goal && profile.activity_level;
  const targets = canRecalc
    ? buildDailyTargets({
        sex: profile.sex!,
        birth_date: profile.birth_date!,
        height_cm: profile.height_cm!,
        weight_kg: weightKg,
        goal: profile.goal!,
        activity_level: profile.activity_level!,
      })
    : {};
  const { data, error } = await supabase
    .from('profiles')
    .update({ weight_kg: weightKg, last_weight_change_at: new Date().toISOString(), ...targets })
    .eq('id', profile.id)
    .select()
    .single();
  if (error) throw error;
  return data as Profile;
}

export type WeightPoint = { weight_kg: number; logged_at: string };

/** Изменение за последние 30 дней: последний замер против первого в окне
 *  (или последнего перед окном, если внутри окна замер один). */
export function monthChange(points: WeightPoint[]): { delta: number; days: number } | null {
  if (points.length < 2) return null;
  const sorted = [...points].sort((a, b) => a.logged_at.localeCompare(b.logged_at));
  const last = sorted[sorted.length - 1];
  const since = Date.now() - 30 * 86400000;
  const inWindow = sorted.filter((p) => new Date(p.logged_at).getTime() >= since);
  const before = sorted.filter((p) => new Date(p.logged_at).getTime() < since);
  const base = inWindow.length >= 2 ? inWindow[0] : before[before.length - 1];
  if (!base || base === last) return null;
  const days = Math.max(1, Math.round((new Date(last.logged_at).getTime() - new Date(base.logged_at).getTime()) / 86400000));
  return { delta: Math.round((last.weight_kg - base.weight_kg) * 10) / 10, days };
}
